import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/shared/integrations/supabase/client';

/** Format classification for documents (from documents table) – used for PDF / 2D / 3D tabs */
export type DocumentFormat = 'pdf' | '2d' | '3d' | 'existingDocs';

export interface UnifiedFile {
  id: string;
  property_id: string;
  filename: string;
  file_path: string;
  file_size: number | null;
  file_type: 'drawing' | 'document' | '3d_model' | 'point_cloud' | 'other';
  category: string;
  /** Raw `documents.category` from the DB; used by classifiedFiles for Site Reports routing. */
  source_category: string | null;
  mime_type: string | null;
  uploaded_at: string;
  uploaded_by?: string;
  description?: string | null;
  download_url?: string;
  /** Set only for file_type === 'document'; used to show in PDF / 2D / 3D tabs */
  document_format?: DocumentFormat;
}

/**
 * Maps the Supabase `documents.category` value (set by the sync)
 * to the UI document_format used for tab classification.
 *
 * Sync writes these categories:
 *   'pdf drawings'       → PDF Drawings tab
 *   '2d drawing'         → 2D Drawings tab
 *   '3d files'           → 3D Files tab
 *   'site reports'       → Site Reports tab (becomes file_type 'drawing')
 *   'existing documents' → Existing Documents tab (all)
 *   'proposal'           → Existing Documents tab (all)
 */
function getDocumentFormatFromCategory(category: string | null): { format: DocumentFormat; fileType: 'document' | 'drawing' } {
  switch (category) {
    case '2d drawing':
    case 'autodesk':
      return { format: '2d', fileType: 'document' };
    case '3d files':
    case 'pointCloud':
      return { format: '3d', fileType: 'document' };
    case 'site reports':
    case 'photos':
      return { format: 'pdf', fileType: 'drawing' };
    case 'site_report':
    case 'site_summary':
    case 'category_summary':
    case 'pdf drawings':
    case 'pdf':
      return { format: 'pdf', fileType: 'document' };
    case 'existing documents':
    case 'existingDocs':
    case 'proposal':
      return { format: 'existingDocs', fileType: 'document' };
    default:
      return { format: 'existingDocs', fileType: 'document' };
  }
}

/** `documents.category` values that belong in the Site Reports tab despite mapping to file_type 'document'. */
const SITE_REPORT_CATEGORIES = ['site_report', 'site_summary', 'category_summary'];

export const usePropertyAllFiles = (propertyId: string) => {
  const queryClient = useQueryClient();

  const { data: allFiles = [], isLoading, error } = useQuery({
    queryKey: ['property-all-files', propertyId],
    queryFn: async () => {
      const files: UnifiedFile[] = [];

      // All file metadata now lives in the `documents` table — classified by the
      // `category` column set by the Firebase sync.
      const { data: documents } = await supabase
        .from('documents')
        .select('*')
        .eq('property_id', propertyId)
        .order('created_at', { ascending: false });

      if (documents) {
        files.push(...documents.map(d => {
          const { format, fileType } = getDocumentFormatFromCategory(d.category);
          return {
            id: d.id,
            property_id: d.property_id ?? '',
            filename: d.document_name,
            file_path: d.file_path || '',
            file_size: d.file_size,
            file_type: fileType as 'document' | 'drawing',
            category: d.document_type || 'Document',
            source_category: d.category,
            mime_type: d.mime_type,
            uploaded_at: d.created_at,
            uploaded_by: d.uploaded_by,
            description: d.description,
            document_format: format,
          };
        }));
      }

      const sorted = files.sort((a, b) =>
        new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime()
      );

      // Deduplicate by file_path (same file may appear under multiple categories)
      const seen = new Set<string>();
      return sorted.filter(f => {
        const key = f.file_path || f.filename;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    enabled: !!propertyId,
  });

  // Classify files by type – each document appears in one tab only
  const classifiedFiles = {
    /** Site Reports tab: file_type 'drawing' (category 'site reports'/'photos') plus the site-report document categories. */
    siteReports: allFiles.filter(f =>
      f.file_type === 'drawing' || SITE_REPORT_CATEGORIES.includes(f.source_category ?? '')
    ),
    /** Existing Documents tab: documents with category='existing documents' or 'proposal' */
    existingDocs: allFiles.filter(f =>
      f.file_type === 'document' && f.document_format === 'existingDocs'
    ),
    /** PDF Drawings tab: documents with category='pdf drawings' (excluding site-report categories). */
    pdfDocuments: allFiles.filter(f =>
      f.file_type === 'document' && f.document_format === 'pdf'
      && !SITE_REPORT_CATEGORIES.includes(f.source_category ?? '')
    ),
    /** 2D Drawings tab: documents with category='2d drawing' */
    twoDFiles: allFiles.filter(f =>
      f.file_type === 'document' && f.document_format === '2d'
    ),
    /** All documents */
    documents: allFiles.filter(f => f.file_type === 'document'),
    models3D: allFiles.filter(f => f.file_type === '3d_model'),
    /** 3D Files tab: documents with category='3d files' */
    threeDFiles: allFiles.filter(f =>
      f.file_type === 'document' && f.document_format === '3d'
    ),
    pointClouds: allFiles.filter(f => f.file_type === 'point_cloud'),
    other: allFiles.filter(f => f.file_type === 'other'),
    all: allFiles,
  };

  return {
    files: classifiedFiles,
    allFiles,
    isLoading,
    error: error?.message || null,
    refetch: () => queryClient.invalidateQueries({ queryKey: ['property-all-files', propertyId] }),
  };
};
